import { Component, EventEmitter, inject, Inject, OnInit, Output } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { ButtonComponent, ModalComponent } from "@evc/web-components";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DatePicker } from "primeng/datepicker";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { MultiSelectModule } from "primeng/multiselect";
import { SelectModule } from "primeng/select";
import { concatMap, take } from "rxjs";
import { Inspection, InspectionDetailUpdate } from "../_objects/inspection";
import { CommonSnackBarService } from "../_services/common-snack-bar.service";
import { InspectionService } from "../_services/http-api/inspection.service";
import { UserPermissionsService } from "../_services/http-api/user-permissions.service";
import { NetworkStatusService } from "../_services/network-status.service";
import { SpinnerService } from "../_services/spinner.service";
import { ConfirmationModalComponent } from "../_shared/component/confirmation-modal/confirmation-modal.component";
import { EVC_MODAL_DATA, EVC_MODAL_REF, EvcModalService } from "../_shared/services/modal/evc-modal.service";
import { RoutePage } from "../app-routes.enum";
import { INSPECTION_STATUS_CODE } from "../app.constants";
import { InspectionFormService } from "../create-inspection-modal/inspection-form.service";
import { InspectionTemplateDownloadService } from "../_services/inspection-template-download.service";
import { StructureService } from "../_services/http-api/structure.service";
import { InspectionStateService } from "../_services/inspection/inspection-state.service";
import { KeyValuePipe } from "@angular/common";
import { SendToIplexGComponent } from "./send-to-iplex-g/send-to-iplex-g.component";

type InspectionFormProps = {
  inspection?: Inspection;
};

@Component({
  selector: "app-inspection-information-modal",
  imports: [
    DialogModule,
    SelectModule,
    MultiSelectModule,
    ButtonComponent,
    InputTextModule,
    ModalComponent,
    ReactiveFormsModule,
    DatePicker,
    TranslateModule,
    KeyValuePipe,
    SendToIplexGComponent
  ],
  templateUrl: "./inspection-information-modal.component.html",
  styleUrl: "./inspection-information-modal.component.scss",
  providers: [InspectionFormService]
})
export class InspectionInformationModalComponent implements OnInit {
  @Output() public inspectionEdited = new EventEmitter<Inspection>();
  private modalRef = inject(EVC_MODAL_REF);

  public titleErrorTranslationKeys: Record<string, string> = {
    required: "inspectionForm.errors.titleRequired",
    maxlength: "inspectionForm.errors.titleMaxLength",
    windowsForbiddenChars: "inspectionForm.errors.titleWindowsForbiddenChars"
  };

  constructor(
    @Inject(EVC_MODAL_DATA) private data: InspectionFormProps | null,
    private modalService: EvcModalService,
    private inspectionService: InspectionService,
    private inspectionStateService: InspectionStateService,
    private translateService: TranslateService,
    private spinnerSerivce: SpinnerService,
    private snackBarService: CommonSnackBarService,
    private userPermission: UserPermissionsService,
    public formService: InspectionFormService,
    private router: Router,
    private inspectionDownloadService: InspectionTemplateDownloadService,
    private structureService: StructureService,
    private networkStatusService: NetworkStatusService,
  ) {}

  get form() {
    return this.formService.form;
  }

  ngOnInit(): void {
    const currentInspection = this.data?.inspection;
    if (currentInspection) {
      this.setFormWithCurrentInspection(currentInspection);
    }
  }

  private setFormWithCurrentInspection(currentInspection: Inspection) {
    this.userPermission.updateUserProperties(currentInspection);
    this.form.controls.title.setValue(currentInspection.title);
    this.form.controls.title.markAsDirty();
    this.form.controls.inspectionDate.setValue(new Date(currentInspection.inspectionDate));
    this.form.controls.inspectors.setValue(currentInspection.inspectors.map((inspector) => inspector.id));
    this.form.controls.liveAsset.setValue(currentInspection.subjectId);

    if (this.isReadOnly(currentInspection)) {
      this.form.disable();
    } else if (currentInspection.status !== INSPECTION_STATUS_CODE.NOTINSPECTED) {
      this.form.controls.liveAsset.disable();
    }
  }

  private isReadOnly(inspection: Inspection) {
    if (inspection.status === INSPECTION_STATUS_CODE.COMPLETED) {
      return true;
    }

    return !this.canManageInspection;
  }

  public get inspection() {
    return this.data.inspection;
  }

  public get isBrianMode() {
    return this.networkStatusService.isBrianMode;
  }

  public get isOnPremise(): boolean {
    return this.networkStatusService.isOnPremise;
  }

  public get canManageInspection(): boolean {
    return this.userPermission.isAdminRole || this.userPermission.isInspectionOwner || this.userPermission.isInspector;
  }

  public save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.editInspection();
  }

  private get editInspectionPayload() {
    const inspectionUpdateDetails = new InspectionDetailUpdate();
    const formValue = this.form.getRawValue();
    inspectionUpdateDetails.id = this.inspection.id;
    inspectionUpdateDetails.title = formValue.title;
    inspectionUpdateDetails.inspectionDate = formValue.inspectionDate.toISOString();
    inspectionUpdateDetails.inspectors = formValue.inspectors.map((id) => ({
      id,
      isActive: true,
      isSubscription: true
    }));
    inspectionUpdateDetails.subjectId = formValue.liveAsset;
    inspectionUpdateDetails.schemaVersion = "0";
    return inspectionUpdateDetails;
  }

  private editInspection() {
    this.spinnerSerivce.show();
    this.inspectionService
      .updateInspection(this.editInspectionPayload)
      .pipe(concatMap(() => this.inspectionStateService.refresh()))
      .subscribe({
        next: () => {
          this.onClose();
          this.spinnerSerivce.hide();
        },
        error: (error) => {
          this.spinnerSerivce.hide();
          this.snackBarService.open(this.translateService.instant("inspectionForm.errors.updateUnknownError"));
          console.error("Error creating inspection:", error);
        }
      });
  }

  onClose() {
    this.modalRef.close();
    this.form.reset();
  }

  onDelete(): void {
    const confirmDeleteRef = this.modalService.showModal(ConfirmationModalComponent, {
      mainLabel: this.translateService.instant("inspectionForm.deleteInspectionyMainMessage"),
      secondaryLabel: this.translateService.instant("inspectionForm.deleteInspectionSecondaryMessage", { title: this.inspection.title })
    });

    confirmDeleteRef.componentRef.instance.confirm.pipe(take(1)).subscribe(() => {
      confirmDeleteRef.close();
      this.deleteInspection();
    });
    confirmDeleteRef.componentRef.instance.reject.pipe(take(1)).subscribe(() => confirmDeleteRef.close());
  }

  deleteInspection(): void {
    this.spinnerSerivce.show();
    this.inspectionService.deleteInspection(this.inspection.id).subscribe({
      next: () => {
        this.modalRef.close();
        this.spinnerSerivce.hide();
        this.router.navigate([RoutePage.InspectionList]);
      },
      error: () => this.snackBarService.open(this.translateService.instant("inspectionForm.errors.delete"))
    });
  }

  async downloadStructure() {
    this.spinnerSerivce.show();
    this.structureService
      .getStructureZip(null, this.inspection.id, true)
      .pipe(concatMap((blob) => this.inspectionDownloadService.downloadInspectionTemplate(blob, this.inspection.title)))
      .subscribe({
        next: () => this.spinnerSerivce.hide(),
        error: (error) => {
          console.error(error);
          this.spinnerSerivce.hide();
          this.snackBarService.open("inspectionForm.errors.downloadStructure");
        }
      });
  }
}
